import React, { useState, useEffect } from 'react';
import { Spinner, Badge } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import InputMask from 'react-input-mask';
import axios from 'axios';

const LeadModal = ({ show, onHide, lead, onSave, colaboradores, currentUser, isSaving, showAlertModal, onOpenScheduleModal, onWhatsAppClick  }) => {
    const [formData, setFormData] = useState({});
    const [newNote, setNewNote] = useState('');
    const [tasks, setTasks] = useState([]);
    const [score, setScore] = useState(0);
    const [newTaskDescription, setNewTaskDescription] = useState('');
    const [newTaskDueDate, setNewTaskDueDate] = useState('');
    const [isConverting, setIsConverting] = useState(false);
    const [activeTab, setActiveTab] = useState('perfil');
    const [isVisible, setIsVisible] = useState(false);
    const [editingField, setEditingField] = useState(null);

    useEffect(() => {
        if (show) {
            setTimeout(() => setIsVisible(true), 10);
            if (lead) {
                setFormData({ ...lead });
                setTasks(lead.tarefas || []);
                setScore(lead.pontuacao || 0);
            } else {
                setFormData({
                    nome: '', telefone: '', email: '', prioridade: 'baixa',
                    status: 'novo', historicos: [], origem_contato: null,
                    respondeu_contato: false, tarefas: [], responsavel_id: '',
                });
                setTasks([]);
                setScore(0);
            }
            setNewNote('');
            setNewTaskDescription('');
            setNewTaskDueDate('');
        } else {
            setIsVisible(false);
        }
    }, [lead, show]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => onHide(), 300);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddTask = () => {
        if (!newTaskDescription || !newTaskDueDate) {
            showAlertModal('Campo Obrigatório', 'Preencha a descrição e a data da tarefa.', 'warning');
            return;
        }
        const newTask = {
            id: `temp-${Date.now()}`,
            descricao: newTaskDescription,
            data_vencimento: newTaskDueDate,
            concluida: 0,
            user_id: currentUser.id,
            user: { nome: currentUser.nome }
        };
        setTasks(prev => [...prev, newTask]);
        setNewTaskDescription('');
        setNewTaskDueDate('');
    };

    const handleToggleTask = (taskId) => {
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, concluida: t.concluida ? 0 : 1 } : t));
    };

    const handleDeleteTask = (taskId) => {
        if (String(taskId).startsWith('temp-')) {
            setTasks(prev => prev.filter(t => t.id !== taskId));
        } else {
            setTasks(prev => prev.map(t => t.id === taskId ? { ...t, _delete: true } : t));
        }
    };

    const handleSave = () => {
        if (formData.telefone && formData.telefone.includes('_')) {
            showAlertModal('Validação', 'Telefone incompleto.', 'warning');
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email)) {
            showAlertModal('Validação', 'Email inválido.', 'warning');
            return;
        }
        onSave({ ...formData, nova_nota: newNote, pontuacao: score, tarefas: tasks });
    };

    const handleSchedule = async () => {
        if (!lead?.id) return;
        setIsConverting(true);
        try {
            const response = await axios.post(`/api/leads/${lead.id}/convert-to-patient`);
            onOpenScheduleModal(response.data.paciente);
            onHide();
        } catch (error) {
            if (error.response?.status === 409) {
                showAlertModal('Conflito de E-mail', error.response.data.message, 'warning');
            } else {
                showAlertModal('Erro', 'Não foi possível converter o lead.', 'danger');
            }
        } finally {
            setIsConverting(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            novo: '#3b82f6', contatado: '#8b5cf6', negociando: '#f97316',
            aguardando: '#eab308', convertido: '#22c55e', perdido: '#6b7280'
        };
        return colors[status] || '#6b7280';
    };

    const getStatusLabel = (status) => {
        const labels = {
            novo: 'Novo', contatado: 'Contatado', negociando: 'Negociando',
            aguardando: 'Aguardando', convertido: 'Convertido', perdido: 'Perdido'
        };
        return labels[status] || status;
    };

    const getPrioridadeColor = (p) => ({ alta: '#ef4444', media: '#f97316', baixa: '#22c55e' }[p] || '#6b7280');

    const avatarLetter = formData.nome?.charAt(0)?.toUpperCase() || '?';
    const statusColor = getStatusColor(formData.status);

    const kanbanSteps = [
        { id: 'novo', label: 'Novo' },
        { id: 'contatado', label: 'Contatado' },
        { id: 'negociando', label: 'Negociando' },
        { id: 'aguardando', label: 'Aguardando' },
        { id: 'convertido', label: 'Convertido' },
        { id: 'perdido', label: 'Perdido' },
    ];

    const currentStepIndex = kanbanSteps.findIndex(s => s.id === formData.status);

    if (!show) return null;

    return (
        <>
            {/* Overlay */}
            <div
                onClick={handleClose}
                style={{
                    position: 'fixed', inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    zIndex: 1040,
                    opacity: isVisible ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                }}
            />

            {/* Drawer */}
            <div style={{
                position: 'fixed',
                top: 0, right: 0, bottom: 0,
                width: '90%', maxWidth: '1100px',
                backgroundColor: '#fff',
                zIndex: 1050,
                display: 'flex',
                transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s ease',
                boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
            }}>

                {/* Botão fechar flutuante */}
                <button
                    onClick={handleClose}
                    style={{
                        position: 'absolute', top: '24px', left: '-18px',
                        width: '36px', height: '36px',
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        zIndex: 10,
                    }}
                >
                    <FontAwesomeIcon icon="times" style={{ fontSize: `calc(14px * var(--font-scale))`, color: '#374151' }} />
                </button>

                {/* Overlay de salvamento */}
                {isSaving && (
                    <div style={{
                        position: 'absolute', inset: 0,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        zIndex: 20,
                    }}>
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <p style={{ marginTop: '12px', color: '#6b7280', fontWeight: 500 }}>Salvando...</p>
                    </div>
                )}

                {/* ===== COLUNA ESQUERDA — Perfil ===== */}
                <div style={{
                    width: '300px', minWidth: '300px',
                    backgroundColor: '#f9fafb',
                    borderRight: '1px solid #e5e7eb',
                    display: 'flex', flexDirection: 'column',
                    overflowY: 'auto',
                }}>
                    {/* Header com gradiente */}
                    <div style={{
                        background: `radial-gradient(circle, ${statusColor}55 0%, ${statusColor}88 100%)`,
                        height: '80px', position: 'relative', flexShrink: 0,
                    }} />

                    {/* Avatar */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '-40px', padding: '0 16px 16px' }}>
                        <div style={{
                            width: '80px', height: '80px', borderRadius: '50%',
                            backgroundColor: `${statusColor}22`,
                            border: `3px solid #fff`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '28px', fontWeight: '300', color: statusColor,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        }}>
                            {avatarLetter}
                        </div>

                        {/* Nome editável */}
                        {editingField === 'nome' ? (
                            <input
                                autoFocus
                                value={formData.nome || ''}
                                name="nome"
                                onChange={handleChange}
                                onBlur={() => setEditingField(null)}
                                style={{
                                    marginTop: '8px', textAlign: 'center',
                                    fontSize: '16px', fontWeight: '600',
                                    border: 'none', borderBottom: '2px solid #3b82f6',
                                    background: 'transparent', outline: 'none',
                                    width: '100%',
                                }}
                            />
                        ) : (
                            <div
                                onClick={() => setEditingField('nome')}
                                style={{
                                    marginTop: '8px', fontSize: '16px', fontWeight: '600',
                                    color: '#111827', cursor: 'pointer', textAlign: 'center',
                                    padding: '2px 8px', borderRadius: '4px',
                                    maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}
                                title="Clique para editar"
                            >
                                {formData.nome || 'Sem nome'}
                            </div>
                        )}

                        {/* Status badge */}
                        <div style={{
                            marginTop: '6px', padding: '2px 10px',
                            borderRadius: '999px', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '500',
                            backgroundColor: `${statusColor}18`, color: statusColor,
                            border: `1px solid ${statusColor}44`,
                        }}>
                            {getStatusLabel(formData.status)}
                        </div>

                        {/* Pontuação */}
                        <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                            {[1, 2, 3, 4, 5].map(star => (
                                <FontAwesomeIcon
                                    key={star}
                                    icon={star <= score ? 'star' : ['far', 'star']}
                                    onClick={() => setScore(star === score ? 0 : star)}
                                    style={{ cursor: 'pointer', color: '#f59e0b', fontSize: `calc(14px * var(--font-scale))` }}
                                />
                            ))}
                        </div>

                        {/* Responsável */}
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: `calc(12px * var(--font-scale))`, color: '#6b7280' }}>
                            <FontAwesomeIcon icon="user-circle" />
                            <select
                                name="responsavel_id"
                                value={formData.responsavel_id || ''}
                                onChange={handleChange}
                                style={{
                                    border: 'none', background: 'transparent',
                                    fontSize: `calc(12px * var(--font-scale))`, color: '#3b82f6', cursor: 'pointer',
                                    outline: 'none', textDecoration: 'underline',
                                }}
                            >
                                <option value="">Sem atendente</option>
                                {colaboradores.map(c => (
                                    <option key={c.id} value={c.id}>{c.nome}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Divider */}
                    <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '0 16px' }} />

                    {/* Notas rápidas */}
                    <div style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                            Notas
                        </div>
                        <textarea
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Adicionar anotação..."
                            rows={3}
                            style={{
                                width: '100%', padding: '8px 12px',
                                fontSize: `calc(13px * var(--font-scale))`, borderRadius: '6px',
                                border: '0 0 0 3px solid #3b82f6',
                                borderLeft: '3px solid #3b82f6',
                                backgroundColor: '#eff6ff',
                                outline: 'none', resize: 'none',
                                color: '#374151',
                            }}
                        />
                    </div>

                    <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '0 16px' }} />

                    {/* Abas do perfil */}
                    <div style={{ padding: '12px 16px', flex: 1 }}>
                        {/* Campos do perfil */}
                        {[
                            { label: 'Telefone', name: 'telefone', type: 'tel', mask: true },
                            { label: 'Email', name: 'email', type: 'email' },
                            { label: 'Prioridade', name: 'prioridade', type: 'select', options: ['baixa', 'media', 'alta'] },
                            { label: 'Origem', name: 'origem_contato', type: 'select', options: [null, 'clinica_iniciou', 'lead_iniciou', 'indicacao'], labels: ['Não especificado', 'Clínica iniciou', 'Lead iniciou', 'Indicação'] },
                        ].map(field => (
                            <div key={field.name} style={{ marginBottom: '12px' }}>
                                <label style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                                    {field.label}
                                </label>
                                {field.type === 'select' ? (
                                    <select
                                        name={field.name}
                                        value={formData[field.name] || ''}
                                        onChange={handleChange}
                                        style={{
                                            width: '100%', border: 'none',
                                            backgroundColor: 'transparent',
                                            fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280',
                                            cursor: 'pointer', outline: 'none',
                                            padding: '2px 0',
                                        }}
                                    >
                                        {field.options.map((opt, i) => (
                                            <option key={i} value={opt || ''}>{field.labels ? field.labels[i] : opt}</option>
                                        ))}
                                    </select>
                                ) : field.mask ? (
                                    <InputMask
                                        mask="(99) 99999-9999"
                                        value={formData[field.name] || ''}
                                        onChange={handleChange}
                                    >
                                        {(inputProps) => (
                                            <input
                                                {...inputProps}
                                                name={field.name}
                                                type={field.type}
                                                placeholder="(xx) xxxxx-xxxx"
                                                style={{
                                                    width: '100%', border: 'none',
                                                    backgroundColor: 'transparent',
                                                    fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280',
                                                    outline: 'none', padding: '2px 0',
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                ) : (
                                    <input
                                        name={field.name}
                                        type={field.type}
                                        value={formData[field.name] || ''}
                                        onChange={handleChange}
                                        placeholder={`Informe o ${field.label.toLowerCase()}`}
                                        style={{
                                            width: '100%', border: 'none',
                                            backgroundColor: 'transparent',
                                            fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280',
                                            outline: 'none', padding: '2px 0',
                                        }}
                                    />
                                )}
                                <div style={{ height: '1px', backgroundColor: '#f3f4f6', marginTop: '4px' }} />
                            </div>
                        ))}

                        {/* Respondeu contato */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                            <label style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '500', color: '#374151' }}>
                                Respondeu ao contato?
                            </label>
                            <div
                                onClick={() => setFormData(prev => ({ ...prev, respondeu_contato: !prev.respondeu_contato }))}
                                style={{
                                    width: '36px', height: '20px', borderRadius: '999px',
                                    backgroundColor: formData.respondeu_contato ? '#22c55e' : '#d1d5db',
                                    cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                                }}
                            >
                                <div style={{
                                    position: 'absolute', top: '2px',
                                    left: formData.respondeu_contato ? '18px' : '2px',
                                    width: '16px', height: '16px', borderRadius: '50%',
                                    backgroundColor: '#fff', transition: 'left 0.2s',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                }} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ===== COLUNA DIREITA — Conteúdo ===== */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                    {/* Pipeline de status */}
                    <div style={{
                        padding: '16px 24px 12px',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#fff',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', paddingBottom: '8px' }}>
                            {kanbanSteps.map((step, index) => {
                                const isActive = step.id === formData.status;
                                const isPast = index < currentStepIndex;
                                const color = getStatusColor(step.id);
                                return (
                                    <React.Fragment key={step.id}>
                                        <div
                                            onClick={() => setFormData(prev => ({ ...prev, status: step.id }))}
                                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', minWidth: '70px' }}
                                        >
                                            <div style={{
                                                width: '28px', height: '28px', borderRadius: '50%',
                                                backgroundColor: isActive ? color : isPast ? `${color}44` : '#f3f4f6',
                                                border: `2px solid ${isActive ? color : isPast ? color : '#d1d5db'}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                transition: 'all 0.2s',
                                            }}>
                                                {isActive && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />}
                                                {isPast && <FontAwesomeIcon icon="check" style={{ fontSize: `calc(10px * var(--font-scale))`, color }} />}
                                            </div>
                                            <span style={{
                                                fontSize: `calc(10px * var(--font-scale))`, marginTop: '4px', textAlign: 'center',
                                                color: isActive ? color : '#9ca3af',
                                                fontWeight: isActive ? '600' : '400',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {step.label}
                                            </span>
                                        </div>
                                        {index < kanbanSteps.length - 1 && (
                                            <div style={{
                                                flex: 1, height: '2px', minWidth: '16px',
                                                backgroundColor: isPast ? getStatusColor(kanbanSteps[index].id) : '#e5e7eb',
                                                marginBottom: '18px', transition: 'background 0.2s',
                                            }} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    {/* Abas */}
                    <div style={{
                        display: 'flex', gap: '0', padding: '0 24px',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#fff',
                    }}>
                        {[
                            { key: 'historico', label: 'Histórico', icon: 'history' },
                            { key: 'tarefas', label: 'Atividades', icon: 'tasks' },
                            { key: 'info', label: 'Informações', icon: 'info-circle' },
                            { key: 'atendimento', label: 'Atendimento', icon: ['fab', 'whatsapp'] },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                style={{
                                    padding: '12px 16px', border: 'none', background: 'none',
                                    cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500',
                                    color: activeTab === tab.key ? '#3b82f6' : '#6b7280',
                                    borderBottom: activeTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
                                    marginBottom: '-1px', transition: 'all 0.15s',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                }}
                            >
                                <FontAwesomeIcon icon={tab.icon} style={{ fontSize: `calc(12px * var(--font-scale))` }} />
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Conteúdo das abas */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>

                        {/* ABA: HISTÓRICO */}
                        {activeTab === 'historico' && (
                            <div>
                                {(formData.historicos || []).length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                                        <FontAwesomeIcon icon="history" style={{ fontSize: '32px', marginBottom: '8px', display: 'block' }} />
                                        Nenhum histórico ainda.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {(formData.historicos || []).map(item => (
                                            <div key={item.id} style={{
                                                backgroundColor: '#f9fafb',
                                                borderRadius: '8px', padding: '12px 16px',
                                                border: '1px solid #f3f4f6',
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                    <span style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: '#374151' }}>
                                                        {item.user?.nome || 'Sistema'}
                                                    </span>
                                                    <span style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
                                                        {format(parseISO(item.created_at), "dd/MM/yyyy 'às' HH:mm")}
                                                    </span>
                                                </div>
                                                <p style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280', margin: 0 }}>{item.nota}</p>

                                                {item.midia_url && item.midia_tipo === 'image' && (
                                                    <a href={item.midia_url} target="_blank" rel="noopener noreferrer">
                                                        <img src={item.midia_url} alt="Imagem" style={{ maxWidth: '200px', borderRadius: '6px', marginTop: '8px' }} />
                                                    </a>
                                                )}
                                                {item.midia_url && item.midia_tipo === 'video' && (
                                                    <video controls style={{ maxWidth: '100%', borderRadius: '6px', marginTop: '8px' }}>
                                                        <source src={item.midia_url} type="video/mp4" />
                                                    </video>
                                                )}
                                                {item.midia_url && item.midia_tipo === 'audio' && (
                                                    <audio controls style={{ width: '100%', marginTop: '8px' }}>
                                                        <source src={item.midia_url} type="audio/ogg" />
                                                    </audio>
                                                )}
                                                {item.midia_url && item.midia_tipo === 'sticker' && (
                                                    <img src={item.midia_url} alt="Sticker" style={{ maxWidth: '80px', marginTop: '8px' }} />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ABA: TAREFAS */}
                        {activeTab === 'tarefas' && (
                            <div>
                                {/* Adicionar tarefa */}
                                <div style={{
                                    backgroundColor: '#f9fafb', borderRadius: '8px',
                                    padding: '12px', marginBottom: '16px',
                                    border: '1px solid #e5e7eb',
                                }}>
                                    <div style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                        Nova Atividade
                                    </div>
                                    <input
                                        value={newTaskDescription}
                                        onChange={(e) => setNewTaskDescription(e.target.value)}
                                        placeholder="Descreva a atividade..."
                                        style={{
                                            width: '100%', padding: '8px 12px',
                                            border: '1px solid #e5e7eb', borderRadius: '6px',
                                            fontSize: `calc(13px * var(--font-scale))`, marginBottom: '8px', outline: 'none',
                                        }}
                                    />
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            type="datetime-local"
                                            value={newTaskDueDate}
                                            onChange={(e) => setNewTaskDueDate(e.target.value)}
                                            style={{
                                                flex: 1, padding: '8px 12px',
                                                border: '1px solid #e5e7eb', borderRadius: '6px',
                                                fontSize: `calc(13px * var(--font-scale))`, outline: 'none',
                                            }}
                                        />
                                        <button
                                            onClick={handleAddTask}
                                            style={{
                                                padding: '8px 16px', backgroundColor: '#3b82f6',
                                                color: '#fff', border: 'none', borderRadius: '6px',
                                                fontSize: `calc(13px * var(--font-scale))`, cursor: 'pointer', fontWeight: '500',
                                            }}
                                        >
                                            + Adicionar
                                        </button>
                                    </div>
                                </div>

                                {/* Lista de tarefas */}
                                {tasks.filter(t => !t._delete).length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                                        <FontAwesomeIcon icon="tasks" style={{ fontSize: '32px', marginBottom: '8px', display: 'block' }} />
                                        Nenhuma atividade ainda.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {tasks.filter(t => !t._delete).map(task => (
                                            <div key={task.id} style={{
                                                display: 'flex', alignItems: 'center', gap: '12px',
                                                padding: '10px 14px', borderRadius: '8px',
                                                backgroundColor: task.concluida ? '#f9fafb' : '#fff',
                                                border: '1px solid #e5e7eb',
                                            }}>
                                                <div
                                                    onClick={() => handleToggleTask(task.id)}
                                                    style={{
                                                        width: '18px', height: '18px', borderRadius: '50%',
                                                        border: `2px solid ${task.concluida ? '#22c55e' : '#d1d5db'}`,
                                                        backgroundColor: task.concluida ? '#22c55e' : 'transparent',
                                                        cursor: 'pointer', flexShrink: 0,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}
                                                >
                                                    {task.concluida && <FontAwesomeIcon icon="check" style={{ fontSize: '9px', color: '#fff' }} />}
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{
                                                        fontSize: `calc(13px * var(--font-scale))`, color: task.concluida ? '#9ca3af' : '#374151',
                                                        textDecoration: task.concluida ? 'line-through' : 'none',
                                                    }}>
                                                        {task.descricao}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '12px', marginTop: '2px' }}>
                                                        <span style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
                                                            <FontAwesomeIcon icon="user" style={{ marginRight: '4px' }} />
                                                            {task.user?.nome || '...'}
                                                        </span>
                                                        <span style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
                                                            <FontAwesomeIcon icon="calendar-alt" style={{ marginRight: '4px' }} />
                                                            {format(parseISO(task.data_vencimento), "dd/MM/yyyy HH:mm")}
                                                        </span>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleDeleteTask(task.id)}
                                                    style={{
                                                        background: 'none', border: 'none',
                                                        color: '#d1d5db', cursor: 'pointer', padding: '4px',
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon="trash" style={{ fontSize: `calc(12px * var(--font-scale))` }} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ABA: INFORMAÇÕES */}
                        {activeTab === 'info' && (
                            <div>
                                {/* Pipeline visual */}
                                <div style={{
                                    backgroundColor: '#f9fafb', borderRadius: '8px',
                                    padding: '16px', marginBottom: '16px',
                                    border: '1px solid #e5e7eb',
                                }}>
                                    <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                                        Jornada do Lead
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto' }}>
                                        {kanbanSteps.map((step, index) => {
                                            const isActive = step.id === formData.status;
                                            const isPast = index < currentStepIndex;
                                            const color = getStatusColor(step.id);
                                            return (
                                                <React.Fragment key={step.id}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '60px' }}>
                                                        <div style={{
                                                            width: '24px', height: '24px', borderRadius: '50%',
                                                            backgroundColor: isActive ? color : isPast ? `${color}44` : '#e5e7eb',
                                                            border: `2px solid ${isActive || isPast ? color : '#d1d5db'}`,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}>
                                                            {isPast && <FontAwesomeIcon icon="check" style={{ fontSize: '9px', color }} />}
                                                        </div>
                                                        <span style={{ fontSize: '9px', marginTop: '4px', color: isActive ? color : '#9ca3af', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                            {step.label}
                                                        </span>
                                                    </div>
                                                    {index < kanbanSteps.length - 1 && (
                                                        <div style={{ flex: 1, height: '2px', backgroundColor: isPast ? color : '#e5e7eb', minWidth: '12px', marginBottom: '14px' }} />
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Métricas */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                                    {[
                                        { label: 'Prioridade', value: formData.prioridade, color: getPrioridadeColor(formData.prioridade), icon: 'flag' },
                                        { label: 'Pontuação', value: `${score}/5`, color: '#f59e0b', icon: 'star' },
                                        { label: 'Tarefas', value: `${tasks.filter(t => !t._delete && !t.concluida).length} pendentes`, color: '#3b82f6', icon: 'tasks' },
                                        { label: 'Histórico', value: `${(formData.historicos || []).length} registros`, color: '#8b5cf6', icon: 'history' },
                                    ].map(metric => (
                                        <div key={metric.label} style={{
                                            backgroundColor: '#fff', borderRadius: '8px',
                                            padding: '12px', border: '1px solid #e5e7eb',
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                        }}>
                                            <div style={{
                                                width: '36px', height: '36px', borderRadius: '8px',
                                                backgroundColor: `${metric.color}18`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <FontAwesomeIcon icon={metric.icon} style={{ color: metric.color, fontSize: `calc(14px * var(--font-scale))` }} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>{metric.label}</div>
                                                <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151', textTransform: 'capitalize' }}>{metric.value}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ABA: ATENDIMENTO */}
                        {activeTab === 'atendimento' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                                {/* Card principal do WhatsApp */}
                                <div style={{
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    overflow: 'hidden',
                                }}>
                                    {/* Header do card */}
                                    <div style={{
                                        background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
                                        padding: '20px 24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{
                                                width: '44px', height: '44px', borderRadius: '50%',
                                                backgroundColor: 'rgba(255,255,255,0.2)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <FontAwesomeIcon icon={['fab', 'whatsapp']} style={{ fontSize: '22px', color: '#fff' }} />
                                            </div>
                                            <div>
                                                <div style={{ color: '#fff', fontWeight: '600', fontSize: `calc(15px * var(--font-scale))` }}>
                                                    WhatsApp
                                                </div>
                                                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: `calc(12px * var(--font-scale))` }}>
                                                    {lead?.telefone || 'Sem telefone cadastrado'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Indicador de mensagens */}
                                        {(formData.historicos || []).filter(h => h.tipo_interacao === 'whatsapp').length > 0 && (
                                            <div style={{
                                                backgroundColor: '#25d366',
                                                borderRadius: '999px',
                                                padding: '4px 10px',
                                                fontSize: `calc(12px * var(--font-scale))`,
                                                color: '#fff',
                                                fontWeight: '600',
                                            }}>
                                                {(formData.historicos || []).filter(h => h.tipo_interacao === 'whatsapp').length} mensagens
                                            </div>
                                        )}
                                    </div>

                                    {/* Preview das últimas mensagens */}
                                    <div style={{ padding: '16px 24px' }}>
                                        {(formData.historicos || []).filter(h => h.tipo_interacao === 'whatsapp' || h.nota?.startsWith('📤')).length === 0 ? (
                                            <div style={{
                                                textAlign: 'center', padding: '24px',
                                                color: '#9ca3af', fontSize: `calc(13px * var(--font-scale))`,
                                            }}>
                                                <FontAwesomeIcon icon={['fab', 'whatsapp']} style={{ fontSize: '32px', color: '#d1d5db', display: 'block', margin: '0 auto 8px' }} />
                                                Nenhuma conversa ainda.<br />
                                                Inicie o atendimento pelo WhatsApp.
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                                                {(formData.historicos || [])
                                                    .filter(h => h.tipo_interacao === 'whatsapp' || h.nota?.startsWith('📤'))
                                                    .slice(-5) // Últimas 5 mensagens
                                                    .map(item => {
                                                        const isEnviada = item.nota?.startsWith('📤');
                                                        return (
                                                            <div key={item.id} style={{
                                                                display: 'flex',
                                                                justifyContent: isEnviada ? 'flex-end' : 'flex-start',
                                                            }}>
                                                                <div style={{
                                                                    maxWidth: '80%',
                                                                    backgroundColor: isEnviada ? '#dcf8c6' : '#f3f4f6',
                                                                    borderRadius: isEnviada ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                                                                    padding: '8px 12px',
                                                                    fontSize: `calc(12px * var(--font-scale))`,
                                                                    color: '#374151',
                                                                }}>
                                                                    <div style={{ wordBreak: 'break-word' }}>{item.nota}</div>
                                                                    {item.midia_url && item.midia_tipo === 'image' && (
                                                                        <img src={item.midia_url} alt="img" style={{ maxWidth: '100px', borderRadius: '4px', marginTop: '4px' }} />
                                                                    )}
                                                                    <div style={{ fontSize: `calc(10px * var(--font-scale))`, color: '#9ca3af', textAlign: 'right', marginTop: '2px' }}>
                                                                        {format(parseISO(item.created_at), "HH:mm")}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </div>
                                        )}
                                    </div>

                                    {/* Botão abrir chat */}
                                    <div style={{ padding: '0 24px 20px' }}>
                                        <button
                                            onClick={() => {
                                                if (onWhatsAppClick && lead) {
                                                    onWhatsAppClick(lead);
                                                    handleClose(); // Fecha o modal ao abrir o chat
                                                }
                                            }}
                                            disabled={!lead?.telefone}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                backgroundColor: lead?.telefone ? '#25d366' : '#d1d5db',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: lead?.telefone ? 'pointer' : 'not-allowed',
                                                fontSize: `calc(14px * var(--font-scale))`,
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                transition: 'background 0.2s',
                                            }}
                                            onMouseEnter={e => { if (lead?.telefone) e.currentTarget.style.backgroundColor = '#1da851' }}
                                            onMouseLeave={e => { if (lead?.telefone) e.currentTarget.style.backgroundColor = '#25d366' }}
                                        >
                                            <FontAwesomeIcon icon={['fab', 'whatsapp']} style={{ fontSize: '18px' }} />
                                            {lead?.telefone ? 'Abrir conversa no WhatsApp' : 'Cadastre um telefone para iniciar'}
                                        </button>
                                    </div>
                                </div>

                                {/* Resumo do atendimento */}
                                <div style={{
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    padding: '16px 24px',
                                }}>
                                    <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                                        Resumo do Atendimento
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                        {[
                                            {
                                                label: 'Mensagens recebidas',
                                                value: (formData.historicos || []).filter(h => h.tipo_interacao === 'whatsapp').length,
                                                color: '#3b82f6',
                                                icon: 'comment',
                                            },
                                            {
                                                label: 'Mensagens enviadas',
                                                value: (formData.historicos || []).filter(h => h.nota?.startsWith('📤')).length,
                                                color: '#22c55e',
                                                icon: 'paper-plane',
                                            },
                                            {
                                                label: 'Total de interações',
                                                value: (formData.historicos || []).length,
                                                color: '#8b5cf6',
                                                icon: 'exchange-alt',
                                            },
                                        ].map(stat => (
                                            <div key={stat.label} style={{
                                                backgroundColor: `${stat.color}08`,
                                                borderRadius: '8px',
                                                padding: '12px',
                                                border: `1px solid ${stat.color}22`,
                                                textAlign: 'center',
                                            }}>
                                                <FontAwesomeIcon icon={stat.icon} style={{ color: stat.color, fontSize: '18px', marginBottom: '6px' }} />
                                                <div style={{ fontSize: '20px', fontWeight: '700', color: stat.color }}>{stat.value}</div>
                                                <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af', marginTop: '2px' }}>{stat.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer com ações */}
                    <div style={{
                        padding: '12px 24px',
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: '#fff',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        {/* Botões de agendamento */}
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {lead && lead.status !== 'convertido' && (
                                <button
                                    onClick={handleSchedule}
                                    disabled={isSaving || isConverting}
                                    style={{
                                        padding: '8px 16px', borderRadius: '6px',
                                        backgroundColor: '#0ea5e9', color: '#fff',
                                        border: 'none', cursor: 'pointer',
                                        fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500',
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        opacity: isSaving || isConverting ? 0.7 : 1,
                                    }}
                                >
                                    {isConverting ? <Spinner as="span" size="sm" animation="border" /> : <FontAwesomeIcon icon="calendar-alt" />}
                                    Agendar
                                </button>
                            )}
                            {lead && lead.status === 'convertido' && (
                                <button
                                    onClick={handleSchedule}
                                    disabled={isSaving || isConverting}
                                    style={{
                                        padding: '8px 16px', borderRadius: '6px',
                                        backgroundColor: '#f59e0b', color: '#fff',
                                        border: 'none', cursor: 'pointer',
                                        fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500',
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                    }}
                                >
                                    <FontAwesomeIcon icon="calendar-check" />
                                    Reagendar
                                </button>
                            )}
                        </div>

                        {/* Salvar / Cancelar */}
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={handleClose}
                                disabled={isSaving}
                                style={{
                                    padding: '8px 16px', borderRadius: '6px',
                                    backgroundColor: '#f3f4f6', color: '#374151',
                                    border: '1px solid #e5e7eb', cursor: 'pointer',
                                    fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                style={{
                                    padding: '8px 20px', borderRadius: '6px',
                                    backgroundColor: '#3b82f6', color: '#fff',
                                    border: 'none', cursor: 'pointer',
                                    fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    opacity: isSaving ? 0.7 : 1,
                                }}
                            >
                                {isSaving ? <Spinner as="span" size="sm" animation="border" /> : <FontAwesomeIcon icon="save" />}
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LeadModal;
